import { useState } from 'react';
import classNames from 'classnames';
import Modal from 'common/components/Modal';
import BEM from 'common/bem';
import Button from 'admin/shared-components/Button';
import { OrganizationSetting } from 'admin/types';
import { actions, thunk, useAppDispatch } from 'redux-stores';
import { ReactComponent as WarningIcon } from 'common/images/warning.svg';

const classes = BEM.with('OrgSettingPropagationModal');

export type OrgSettingPropagationModalProps = {
  closeModal: () => void;
  isOpen: boolean;
  options: {
    updatedSetting: OrganizationSetting;
    previousValue: number | string;
    settingName: 'messageLifespan' | 'pinLock';
    headerText: string;
  };
};

const LABELS = {
  DEFAULT_VALUE: {
    title: 'Change only the default value',
    description:
      'The existing users will keep their current settings. It will only affect new users.',
  },
  APPLY_TO_ALL: {
    title: 'Overwrite settings for all users',
    description: 'This action will update the settings for all existing and future users.',
  },
};
const TOASTER_INSTRUCTIONS = 'Select how you want to apply this change for your organization.';

const ModalContent = ({ closeModal, options, isOpen }: OrgSettingPropagationModalProps) => {
  const dispatch = useAppDispatch();
  const { updatedSetting, previousValue, settingName, headerText } = options;
  const [propagateToAll, setPropagateToAll] = useState<boolean | undefined>(undefined);
  const handleCancel = () => {
    dispatch(actions.updateSettings({ [settingName]: previousValue } as OrganizationSetting));
    dispatch(actions.setModal());
  };

  const handleApplyToAll = () => {
    setPropagateToAll(true);
    dispatch(actions.updateSettings({ ...updatedSetting }));
  };

  const handleKeepExisting = () => {
    setPropagateToAll(false);
    dispatch(actions.updateSettings({ ...updatedSetting }));
  };

  const handleSaveSettings = async () => {
    if (propagateToAll === undefined) return;
    dispatch(actions.setPropagationPreference({ settingName, propagateToAll: propagateToAll }));
    dispatch(actions.updateSettings({ ...updatedSetting }));
    await thunk.saveSettings({ dispatch });
    dispatch(actions.setModal());
  };

  return (
    <Modal
      id="org-setting-propagation-modal"
      bodyClass={classes('body')}
      closeClass={classes('close-button')}
      footerClass={classes('footer')}
      header={
        <div className={classes('title')}>
          <h2>{headerText}</h2>
        </div>
      }
      headerClass={classes('header')}
      headerTextStyle={classes('header-text')}
      isOpen={isOpen}
      size="medium"
      footerSecondaryActions={
        <div className={classes('footer-actions')}>
          <Button variant="secondary" size="medium" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="action"
            size="medium"
            onClick={handleSaveSettings}
            disabled={propagateToAll === undefined}
          >
            Save Settings
          </Button>
        </div>
      }
      onRequestClose={handleCancel}
      borderStyle="rounded"
      className={classes()}
    >
      <div className={classes('options')}>
        <div className={classes('option-row')}>
          <div className={classes('toaster-instructions')}>
            <WarningIcon />
            <p>{TOASTER_INSTRUCTIONS}</p>
          </div>
        </div>
        <div className={classes('option-row')}>
          <button
            className={classNames(classes('option'), {
              [classes('option-selected')]: propagateToAll === true,
            })}
            onClick={handleApplyToAll}
            data-testid="propagation-apply-to-all"
          >
            <p className={classes('option-title')}>{LABELS.APPLY_TO_ALL.title}</p>
            <p className={classes('option-description')}>{LABELS.APPLY_TO_ALL.description}</p>
          </button>
        </div>
        <div className={classes('option-row')}>
          <button
            className={classNames(classes('option'), {
              [classes('option-selected')]: propagateToAll === false,
            })}
            onClick={handleKeepExisting}
            data-testid="propagation-keep-existing"
          >
            <p className={classes('option-title')}>{LABELS.DEFAULT_VALUE.title} </p>
            <p className={classes('option-description')}>{LABELS.DEFAULT_VALUE.description}</p>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ModalContent;
